(() => {
  const VAT_MULTIPLIER = 1.2;
  const INCOMPATIBLE_TITLE = 'Disponible en changeant les autres options';
  const UNAVAILABLE_TITLE = 'Cette option n’est actuellement disponible avec aucune variante';

  const formatMoney = (cents, currency) => new Intl.NumberFormat(
    document.documentElement.lang || 'fr-FR',
    { style: 'currency', currency }
  ).format((Number(cents) || 0) / 100);

  const sameOptions = (variant, values) => (
    variant?.options?.every((value, index) => value === values[index])
  );

  const initProduct = (root) => {
    if (!root || root.dataset.variantReady === 'true') return;

    const json = root.querySelector('[data-product-variants-json]');
    if (!json) return;

    let variants = [];
    try {
      variants = JSON.parse(json.textContent || '[]');
    } catch (error) {
      return;
    }

    root.dataset.variantReady = 'true';

    const referenceJson = root.querySelector('[data-product-reference-json]');
    let referenceEntries = [];
    try {
      referenceEntries = JSON.parse(referenceJson?.textContent || '[]');
    } catch (error) {
      referenceEntries = [];
    }
    const manufacturerReferences = new Map(
      referenceEntries.map((entry) => [String(entry.id), entry.manufacturerReference || ''])
    );

    const availableVariants = variants.filter((variant) => variant.available);
    const optionGroups = [...root.querySelectorAll('[data-option-position]')]
      .sort((first, second) => Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition));
    const idInputs = root.querySelectorAll('[data-variant-id-input]');
    const priceHt = root.querySelector('[data-product-price-ht]');
    const priceTtc = root.querySelector('[data-product-price-ttc]');
    const priceBox = root.querySelector('[data-product-discount-rate]');
    const compareWrap = root.querySelector('[data-product-compare-wrap]');
    const compare = root.querySelector('[data-product-compare-price]');
    const reference = root.querySelector('[data-product-reference]') || root.querySelector('[data-product-sku]');
    const stock = root.querySelector('[data-product-stock]');
    const buttons = root.querySelectorAll('[data-product-submit]');
    const currency = root.dataset.currency || 'EUR';
    const buyLabel = root.dataset.addLabel || 'Acheter';
    const unavailableLabel = root.dataset.unavailableLabel || 'Indisponible';
    const discountRate = Math.max(0, Math.min(100, Number(priceBox?.dataset.productDiscountRate) || 0));
    const discountedPrice = (cents) => Math.round((Number(cents) || 0) * (100 - discountRate) / 100);

    // ConfigFlow updates the HT node directly when a custom cut or machining is chosen.
    // Keep the TTC display aligned with that calculated amount.
    const parseDisplayedPrice = (value) => {
      const raw = String(value || '').replace(/[^0-9,.-]/g, '');
      if (!raw) return null;
      const normalized = raw.lastIndexOf(',') > raw.lastIndexOf('.')
        ? raw.replace(/\./g, '').replace(',', '.')
        : raw.replace(/,/g, '');
      const amount = Number.parseFloat(normalized);
      return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    };

    let lastRenderedHt = '';

    const renderPricePair = (catalogPriceCents) => {
      if (!priceHt || !priceTtc) return;
      const priceCents = discountedPrice(catalogPriceCents);
      lastRenderedHt = formatMoney(priceCents, currency);
      priceHt.textContent = lastRenderedHt;
      priceTtc.textContent = formatMoney(Math.round(priceCents * VAT_MULTIPLIER), currency);

      if (discountRate > 0) {
        if (compareWrap) compareWrap.hidden = false;
        if (compare) compare.textContent = formatMoney(catalogPriceCents, currency);
      }
    };

    const syncDiscountedPriceFromConfigurator = () => {
      if (!priceHt || !priceTtc || priceHt.textContent === lastRenderedHt) return;
      const catalogPriceCents = parseDisplayedPrice(priceHt.textContent);
      if (catalogPriceCents === null) return;
      renderPricePair(catalogPriceCents);
    };

    if (priceHt && priceTtc) {
      lastRenderedHt = priceHt.textContent;
      new MutationObserver(syncDiscountedPriceFromConfigurator).observe(priceHt, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    let activeGroupIndex = 0;
    let currentVariantId = '';

    const selectedValues = () => optionGroups.map((group) => (
      group.querySelector('[data-option-value]:checked')?.value || ''
    ));

    const setSelections = (variant) => {
      if (!variant || optionGroups.length === 0) return;

      optionGroups.forEach((group, index) => {
        const matchingInput = [...group.querySelectorAll('[data-option-value]')]
          .find((input) => input.value === variant.options[index]);

        if (matchingInput) matchingInput.checked = true;
      });
    };

    const setUnavailable = () => {
      currentVariantId = '';
      idInputs.forEach((input) => { input.value = ''; });
      if (stock) stock.textContent = root.dataset.outStock || unavailableLabel;
      buttons.forEach((button) => {
        button.disabled = true;
        button.textContent = unavailableLabel;
      });
    };

    const renderVariant = (variant) => {
      if (!variant) {
        setUnavailable();
        return;
      }

      currentVariantId = String(variant.id);
      const priceCents = Number(variant.price) || 0;
      const compareCents = Number(variant.compare_at_price) || 0;
      const hasComparePrice = discountRate > 0 || compareCents > priceCents;

      idInputs.forEach((input) => { input.value = variant.id; });
      renderPricePair(priceCents);
      if (compareWrap) compareWrap.hidden = !hasComparePrice;
      if (compare) {
        compare.textContent = discountRate > 0
          ? formatMoney(priceCents, currency)
          : (hasComparePrice ? formatMoney(compareCents, currency) : '');
      }
      if (reference) {
        reference.textContent = reference.dataset.referenceSource === 'manufacturer'
          ? (manufacturerReferences.get(String(variant.id)) || '—')
          : (variant.sku || '—');
      }
      if (stock) {
        stock.textContent = variant.available
          ? (root.dataset.inStock || 'Disponible')
          : (root.dataset.outStock || unavailableLabel);
      }

      buttons.forEach((button) => {
        button.disabled = !variant.available;
        button.textContent = variant.available ? buyLabel : unavailableLabel;
      });

      if (variant.featured_media?.id) {
        const gallery = root.querySelector('[data-product-media-gallery]');
        gallery?.cd13ActivateMedia?.(String(variant.featured_media.id), {
          announce: false,
          source: 'variant',
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      root.dispatchEvent(new CustomEvent('cd13:variant-change', {
        bubbles: true,
        detail: { variant },
      }));
    };

    const candidateScore = (variant, desiredValues, anchorIndex) => {
      let matches = 0;
      let priority = 0;

      desiredValues.forEach((value, index) => {
        if (index === anchorIndex || !value || variant.options[index] !== value) return;
        matches += 1;
        priority += optionGroups.length - index;
      });

      return (matches * 1000) + priority;
    };

    const bestVariantFor = (groupIndex, value, desiredValues) => {
      const candidates = availableVariants.filter((variant) => (
        variant.options[groupIndex] === value
      ));

      if (candidates.length === 0) return null;

      return candidates.reduce((best, variant) => {
        const score = candidateScore(variant, desiredValues, groupIndex);
        if (!best || score > best.score) return { variant, score };
        return best;
      }, null).variant;
    };

    const bestVariantForMedia = (mediaId) => {
      const desiredValues = selectedValues();
      const candidates = variants.filter((variant) => (
        String(variant.featured_media?.id || '') === String(mediaId)
      ));

      if (candidates.length === 0) return null;

      return candidates.reduce((best, variant) => {
        const optionMatches = variant.options.reduce((total, value, index) => (
          total + (desiredValues[index] && desiredValues[index] === value ? 1 : 0)
        ), 0);
        const score = (variant.available ? 10000 : 0) + (optionMatches * 100);

        if (!best || score > best.score) return { variant, score };
        return best;
      }, null).variant;
    };

    const updateOptionStates = () => {
      if (optionGroups.length === 0) return;

      const anchorGroup = optionGroups[activeGroupIndex] || optionGroups[0];
      const anchorValue = anchorGroup.querySelector('[data-option-value]:checked')?.value || '';

      optionGroups.forEach((group, groupIndex) => {
        group.classList.toggle('is-active-context', groupIndex === activeGroupIndex);

        group.querySelectorAll('[data-option-value]').forEach((input) => {
          const label = input.nextElementSibling;
          const globallyAvailable = availableVariants.some((variant) => (
            variant.options[groupIndex] === input.value
          ));
          const compatibleWithAnchor = globallyAvailable && (
            groupIndex === activeGroupIndex
            || availableVariants.some((variant) => (
              variant.options[activeGroupIndex] === anchorValue
              && variant.options[groupIndex] === input.value
            ))
          );

          input.disabled = !globallyAvailable;
          input.setAttribute('aria-disabled', String(!globallyAvailable));

          if (!label) return;

          label.classList.toggle('is-incompatible', globallyAvailable && !compatibleWithAnchor);
          label.classList.toggle('is-unavailable', !globallyAvailable);
          label.dataset.optionState = !globallyAvailable
            ? 'unavailable'
            : compatibleWithAnchor
              ? 'compatible'
              : 'incompatible';
          label.title = !globallyAvailable
            ? UNAVAILABLE_TITLE
            : compatibleWithAnchor
              ? ''
              : INCOMPATIBLE_TITLE;
        });
      });
    };

    const chooseFromInput = (input) => {
      if (!input || input.disabled) return;

      const group = input.closest('[data-option-position]');
      const groupIndex = optionGroups.indexOf(group);
      if (groupIndex < 0) return;

      const desiredValues = selectedValues();
      desiredValues[groupIndex] = input.value;

      const variant = bestVariantFor(groupIndex, input.value, desiredValues);
      if (!variant) return;

      activeGroupIndex = groupIndex;
      setSelections(variant);
      renderVariant(variant);
      updateOptionStates();
    };

    optionGroups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const input = event.target.closest('[data-option-value]');
        if (!input || !group.contains(input)) return;
        chooseFromInput(input);
      });
    });

    root.addEventListener('cd13:media-change', (event) => {
      const mediaId = event.detail?.mediaId;
      if (!mediaId) return;

      const variant = bestVariantForMedia(mediaId);
      if (!variant || String(variant.id) === currentVariantId) return;

      setSelections(variant);
      renderVariant(variant);
      updateOptionStates();
    });

    const initialValues = selectedValues();
    const initialVariant = availableVariants.find((variant) => sameOptions(variant, initialValues))
      || availableVariants[0]
      || variants.find((variant) => sameOptions(variant, initialValues))
      || variants[0];

    if (initialVariant) {
      setSelections(initialVariant);
      renderVariant(initialVariant);
    } else {
      setUnavailable();
    }

    updateOptionStates();
  };

  const init = (scope = document) => {
    const roots = scope.matches?.('[data-product-root]')
      ? [scope]
      : scope.querySelectorAll('[data-product-root]');

    roots.forEach(initProduct);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
